/**
 * Property detail guided tour steps.
 *
 * IMPORTANT: When you change property tabs, schedule, checklist, inventory,
 * maintenance, or settings UI, update these steps and bump
 * TOUR_VERSIONS["owner-property-detail"] in tourState.ts.
 */

import type { BeforeHook, Step } from "react-joyride";
import type { PropertyTabId } from "@/lib/propertyTabs";
import {
  parseTargetSelectors,
  waitForAnyTourTarget,
} from "@/lib/waitForTourTarget";

const SCHEDULE_VIEW_KEY = "schedule_view";
const TARGET_WAIT_MS = 6000;

const TAB_LABELS: Record<PropertyTabId, string> = {
  overview: "Overview",
  schedule: "Schedule",
  "my-tasks": "My Tasks",
  checklist: "Checklist",
  inventory: "Inventory",
  unit: "Unit",
  rooms: "Rooms",
  maintenance: "Maintenance",
  payments: "Payments",
  reports: "Reports",
  performance: "Performance",
  automations: "Automations",
  settings: "Settings",
  billing: "Payments",
  invoices: "Payments",
};

export interface PropertyDetailTourContext {
  hasIcal: boolean;
  hasScheduleEntries: boolean;
  hasChecklists: boolean;
  hasInventoryItems: boolean;
  allowedTabs: PropertyTabId[];
}

function switchTabBefore(
  tab: PropertyTabId,
  onTabChange: (tab: PropertyTabId) => void,
  target: string,
  propertyId?: string,
  alternateTargets: string[] = []
): BeforeHook {
  return async () => {
    onTabChange(tab);
    if (tab === "schedule" && propertyId) {
      try {
        localStorage.setItem(`${SCHEDULE_VIEW_KEY}:${propertyId}`, "timeline");
      } catch {
        /* ignore */
      }
    }

    const scrollRoot = document.querySelector("[data-tour-scroll-root]");
    scrollRoot?.scrollTo({ top: 0, behavior: "instant" });

    await new Promise((resolve) => setTimeout(resolve, 50));

    const selectors = [
      ...parseTargetSelectors(target),
      ...alternateTargets,
    ];
    await waitForAnyTourTarget(selectors, TARGET_WAIT_MS);
  };
}

type TourStepInput = Step & {
  /** When set, switches to this tab before the step is shown */
  tab?: PropertyTabId;
  waitTargets?: string[];
};

function step(
  partial: TourStepInput,
  onTabChange: (tab: PropertyTabId) => void,
  propertyId?: string
): Step {
  const { tab, waitTargets, target, ...rest } = partial;
  const targetStr = typeof target === "string" ? target : "body";
  return {
    placement: "bottom",
    skipBeacon: true,
    targetWaitTimeout: TARGET_WAIT_MS,
    ...rest,
    target,
    ...(tab
      ? {
          before: switchTabBefore(
            tab,
            onTabChange,
            targetStr,
            propertyId,
            waitTargets
          ),
        }
      : {}),
  };
}

/** Highlights a sidebar tab; pressing Next on the following step opens that tab. */
function sidebarIntroStep(
  tab: PropertyTabId,
  blurb: string
): TourStepInput {
  const label = TAB_LABELS[tab] ?? tab;
  return {
    target: `[data-tour="property-${tab}-tab"]`,
    title: `Open ${label}`,
    content: `Press **Next** to open **${label}**. ${blurb}`,
    placement: "right",
  };
}

function tabAllowed(ctx: PropertyDetailTourContext, tab: PropertyTabId): boolean {
  return ctx.allowedTabs.includes(tab);
}

export function buildPropertyDetailTourSteps(
  ctx: PropertyDetailTourContext,
  onTabChange: (tab: PropertyTabId) => void,
  propertyId?: string
): Step[] {
  const steps: Step[] = [];

  if (tabAllowed(ctx, "overview")) {
    steps.push(
      step(
        {
          target: '[data-tour="property-overview-tab"]',
          title: "Overview tab",
          content:
            "You are on **Overview** — the home base for this property. Press **Next** to explore what you can do here.",
          placement: "right",
          tab: "overview",
        },
        onTabChange,
        propertyId
      ),
      step(
        {
          target: '[data-tour="tour-overview-metrics"]',
          title: "Key metrics",
          content:
            "These cards summarize upcoming cleanings, your last completed job, open maintenance issues, and low inventory — tap any card to jump to that section.",
          tab: "overview",
        },
        onTabChange,
        propertyId
      ),
      step(
        {
          target: '[data-tour="tour-overview-activity"]',
          title: "Activity & alerts",
          content:
            "See upcoming and recent cleans, open issues, and low-stock items.",
          tab: "overview",
        },
        onTabChange,
        propertyId
      )
    );
  }

  if (tabAllowed(ctx, "schedule")) {
    steps.push(
      step(
        sidebarIntroStep(
          "schedule",
          "This is where turnovers, calendar sync, and cleaner assignments live."
        ),
        onTabChange,
        propertyId
      ),
      ...(!ctx.hasScheduleEntries
        ? [
            step(
              {
                target: '[data-tour="tour-schedule-empty"]',
                waitTargets: [
                  '[data-tour="tour-schedule-view-toggle"]',
                  '[data-tour="tour-schedule-main"]',
                ],
                title: "Connect or add your schedule",
                content: ctx.hasIcal
                  ? "Your calendar is linked but no events are synced yet. Use Sync Calendar after saving iCal URLs in Settings, or add a reservation manually."
                  : "Connect Airbnb, VRBO, or Booking.com iCal in Settings, or add reservations and cleaning tasks manually with the buttons here.",
                tab: "schedule",
              },
              onTabChange,
              propertyId
            ),
          ]
        : [
            step(
              {
                target: '[data-tour="tour-schedule-view-toggle"]',
                waitTargets: [
                  '[data-tour="tour-schedule-empty"]',
                  '[data-tour="tour-schedule-main"]',
                ],
                title: "Timeline or calendar",
                content:
                  "Switch between Timeline (list view) and Calendar (month grid). Drag tasks on the calendar to reschedule.",
                tab: "schedule",
              },
              onTabChange,
              propertyId
            ),
            step(
              {
                target: '[data-tour="tour-schedule-main"]',
                title: "Your schedule",
                content:
                  "Each row is a reservation or cleaning task. Open a task to change the checklist, assign a cleaner, or message your team. Use Add to create a reservation or standalone task.",
                tab: "schedule",
              },
              onTabChange,
              propertyId
            ),
          ])
    );
  }

  if (tabAllowed(ctx, "checklist")) {
    steps.push(
      step(
        sidebarIntroStep(
          "checklist",
          "Build the step-by-step guide cleaners follow on every turnover."
        ),
        onTabChange,
        propertyId
      ),
      step(
        {
          target: ctx.hasChecklists
            ? '[data-tour="tour-checklist-header"]'
            : '[data-tour="tour-checklist-empty"]',
          waitTargets: [
            '[data-tour="tour-checklist-header"]',
            '[data-tour="tour-checklist-empty"]',
          ],
          title: ctx.hasChecklists ? "Your checklists" : "Create your first checklist",
          content: ctx.hasChecklists
            ? "Each checklist can have rooms, tasks, arrival steps, and proof requirements. Star the default checklist your team should use most often."
            : "Start with Create Checklist — add rooms and tasks, then require photos where quality matters.",
          tab: "checklist",
        },
        onTabChange,
        propertyId
      )
    );
  }

  if (tabAllowed(ctx, "inventory")) {
    steps.push(
      step(
        sidebarIntroStep(
          "inventory",
          "Track supplies by room, set restock thresholds, and get low-stock alerts."
        ),
        onTabChange,
        propertyId
      ),
      step(
        {
          target: '[data-tour="tour-inventory-root"]',
          waitTargets: ['[data-tour="tour-inventory-toolbar"]'],
          title: ctx.hasInventoryItems ? "Manage supplies" : "Add your first items",
          content: ctx.hasInventoryItems
            ? "Search items, scan a room with AI, or add items manually. Low-stock and reorder tools appear at the top when you need them."
            : "Use Add Item or Scan room with AI to stock the unit. Assign items to rooms so cleaners know where everything lives.",
          tab: "inventory",
        },
        onTabChange,
        propertyId
      )
    );
  }

  if (tabAllowed(ctx, "maintenance")) {
    steps.push(
      step(
        sidebarIntroStep(
          "maintenance",
          "Damage reports, vendor work orders, and recurring preventative tasks live here."
        ),
        onTabChange,
        propertyId
      ),
      step(
        {
          target: '[data-tour="tour-maintenance-subtabs"]',
          title: "Issues, preventative & appliances",
          content:
            "Issues — cleaner-reported damage and owner-created tickets. Preventative — recurring maintenance on a schedule. Appliances — track warranties and service history.",
          tab: "maintenance",
        },
        onTabChange,
        propertyId
      ),
      step(
        {
          target: '[data-tour="tour-maintenance-new-issue"]',
          title: "Log maintenance",
          content:
            "Create a new issue anytime, assign severity, attach photos, and send vendors a work-order link.",
          tab: "maintenance",
        },
        onTabChange,
        propertyId
      )
    );
  }

  if (tabAllowed(ctx, "settings")) {
    steps.push(
      step(
        sidebarIntroStep(
          "settings",
          "Property details, calendar sync, access instructions, and your team."
        ),
        onTabChange,
        propertyId
      ),
      step(
        {
          target: '[data-tour="tour-settings-ical"]',
          title: "Calendar sync (iCal)",
          content:
            "Paste Airbnb, VRBO, or Booking.com iCal links here. After saving, open Schedule and tap Sync Calendar to pull bookings.",
          tab: "settings",
        },
        onTabChange,
        propertyId
      ),
      step(
        {
          target: '[data-tour="tour-access-and-team"]',
          title: "Your team",
          content:
            "Invite cleaners and team admins. Each person only sees properties and tasks you grant them access to.",
          tab: "settings",
        },
        onTabChange,
        propertyId
      )
    );
  }

  return steps;
}
